<template>
    <div class="date-selector">
        <SquareBtn :active="isToday"
                   :color="(isToday ? 'info' : 'white')"
                   class="date-selector__day-btn"
                   @click="setDate(today)">
            {{ l('TODAY') }}
        </SquareBtn>

        <div class="btn-group-flex flex--align-stretch flex--grow">
            <SquareBtn color="white"
                       class="date-selector__arrow-btn"
                       :disabled="!canGoToPreviousDate"
                       @click="previousDate">
                <i class="fas fa-chevron-left btn-icon"></i>
            </SquareBtn>

            <DtDatePick v-model="ymdDateString"
                        format="YYYY-MM-DD"
                        class="date-selector__date-pick"
                        :is-date-disabled="isDatePickerDateDisabled">
                <template v-slot:default="{ toggle }">
                    <span class="date-selector__date-display"
                          @click="toggle()">
                        <i class="far fa-calendar-days fa-fw" /> {{ formattedDate }}
                    </span>
                </template>
            </DtDatePick>

            <SquareBtn color="white"
                       class="date-selector__arrow-btn"
                       :disabled="!canGoToNextDate"
                       @click="nextDate">
                <i class="fas fa-chevron-right btn-icon"></i>
            </SquareBtn>
        </div>

        <SquareBtn class="date-selector__day-btn"
                   :active="isTomorrow"
                   :color="(isTomorrow ? 'info' : 'white')"
                   @click="setDate(tomorrow)">
            {{ l('TOMORROW') }}
        </SquareBtn>
    </div>
</template>

<script>
import DateFormat from '@/lib/Dates/DateFormat';
import DtDatePick from '@/vue/components/DatePicker/DtDatePick';
import moment from 'moment';
import SquareBtn from '@/vue/components/SquareBtn';
import { fromYMD, getTodayMidnightDate, getTomorrowMidnightDate, toYMD } from '@/functions/date';

export default {
    name: 'DateSelector',

    components: {
        SquareBtn,
        DtDatePick,
    },

    props: {
        allowPastDates: {
            type: Boolean,
            default: true,
        },

        initialDate: {
            type: Date,
            required: true,
        },
    },

    emits: [
        'change',
    ],

    data() {
        return {
            // vue-date-pick will change this value. We'll parse it and set it as this.selectedDate.
            ymdDateString: toYMD(this.initialDate),

            /**
             * @type {Date}
             */
            selectedDate: this.initialDate,

            // A non-changing variable used to refer to today.
            today: getTodayMidnightDate(),

            // A non-changing variable used to refer to tomorrow.
            tomorrow: getTomorrowMidnightDate(),
        };
    },

    computed: {
        formattedDate() {
            return moment(this.selectedDate).format(DateFormat.DATE_ALTERNATE);
        },

        isToday() {
            return toYMD(this.selectedDate) === toYMD(this.today);
        },

        isTomorrow() {
            return toYMD(this.selectedDate) === toYMD(this.tomorrow);
        },

        canGoToPreviousDate() {
            return !this.isToday || this.allowPastDates;
        },

        canGoToNextDate() {
            // Nothing prevents this currently.
            return true;
        },
    },

    watch: {
        ymdDateString(newValue) {
            const date = fromYMD(newValue);
            this.setDate(date);
        },
    },

    methods: {
        previousDate() {
            this.setDate(moment(this.selectedDate).subtract(1, 'days').toDate());
        },

        nextDate() {
            this.setDate(moment(this.selectedDate).add(1, 'days').toDate());
        },

        /**
         * @param {Date} newDate
         */
        setDate(newDate) {
            this.selectedDate = newDate;
            this.ymdDateString = toYMD(newDate);

            this.$emit('change', newDate);
        },

        /**
         * @param {Date} date
         * @return {boolean}
         */
        isDatePickerDateDisabled(date) {
            return !this.allowPastDates && date < this.today;
        },
    },
};
</script>

<style lang="less">
@import "../../../../../less/variables";

.date-selector {
    display: flex;
    justify-content: center;
    align-items: center;
    column-gap: 10px;

    &__date-pick {
        flex-grow: 1;
    }

    &__date-display {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        background: @white;
        color: @text-color;
        padding: 0 @gutter-width;
        border: 1px @btn-default-border solid;
        border-right: 0;
        font-size: 15px;

        // Clicking this opens the date picker, so make it look clickable.
        cursor: pointer;

        small {
            font-size: 11px;
            line-height: 14px;
        }

        strong {
            font-size: @font-size-medium;
            line-height: 15px;
        }

        i {
            margin-right: 5px;
        }
    }

    .btn {
        flex-grow: 0;
        flex-shrink: 0;
    }

    // Today / Tomorrow buttons.
    &__day-btn {
        width: 97px;
    }

    &__date_pick,
    &__date-display,
    .btn {
        height: 40px;
    }
}
</style>
